import GameObject from '../../../GameObject/GameObject';
import AnimationClip from '../AnimationClip';
import ElementComponent from '../../../Component/UI/Element';
import { Tween, Easing } from '@tweenjs/tween.js';
import Renderable from '../../../Component/Renderable';
import { delay } from '../../../Utility/Task/TaskQueue';

interface IShopMenuData {
    products: GameObject[];
    specialOffer: GameObject;
    specialJoker: GameObject;
}

export class ShopMenu extends AnimationClip {
    public *execute(
        gameObject: GameObject,
        meta: IShopMenuData
    ): IterableIterator<number> {
        // Flip all products.
        for (const product of meta.products) {
            yield* this.flip(product);
        }

        // Rotate shines.
        meta.specialOffer.children.forEach((child: GameObject) => {
            this.shining(child);
        });
        meta.specialJoker.children.forEach((child: GameObject) => {
            this.shining(child);
        });

        // Animate Special Offer badge.
        const badgeElement = meta.specialOffer
            .find('badge')
            .getComponent(ElementComponent);
        const iconAngleTween: Tween = this.getTween(badgeElement);
        const iconScaleTween: Tween = this.getTween(badgeElement.scale);

        const amp = (10 / 180) * Math.PI;

        while (true) {
            yield* delay(2000);
            yield* this.scaleTo(iconScaleTween, 1.1, 200);
            yield* this.rotateTo(iconAngleTween, -amp, 200);
            yield* this.rotateTo(iconAngleTween, amp, 200);
            yield* this.rotateTo(iconAngleTween, -amp, 200);
            yield* this.rotateTo(iconAngleTween, amp, 200);
            yield* this.rotateTo(iconAngleTween, 0, 200);
            yield* this.scaleTo(iconScaleTween, 1, 400);
        }
    }

    private *flip(offer: GameObject): IterableIterator<number> {
        const element: ElementComponent = offer.getComponent(ElementComponent);

        const tween: Tween = this.getTween(element.scale);
        tween
            .to({ x: 1 }, 600)
            .easing(Easing.Exponential.Out)
            .start(this.elapsedTime);

        yield* delay(50);

        offer.children.forEach((child: GameObject, index: number) => {
            child.getComponent(Renderable).visible = true;
            this.bounce(child, index);
        });
    }

    private bounce(item: GameObject, index: number): void {
        const element: ElementComponent = item.getComponent(ElementComponent);
        element.scale.set(0, 0);

        const tween: Tween = this.getTween(element.scale);
        tween
            .to({ x: 1, y: 1 }, 600)
            .easing(Easing.Elastic.Out)
            .delay(100 + index * 100)
            .start(this.elapsedTime);
    }

    private shining(item: GameObject): void {
        const shines = item.getChildrenByName('shine');
        if (shines.length === 0) return;

        shines.forEach(shine => {
            const elem: ElementComponent = shine.getComponent(ElementComponent);
            elem.rotation = 0;

            const tweenRotation: Tween = this.getTween(elem);
            tweenRotation
                .to({ rotation: Math.PI * 2 }, 5000)
                .repeat(Infinity)
                .start(this.elapsedTime);
        });
    }

    private *scaleTo(
        tween: Tween,
        scale: number,
        duration: number
    ): IterableIterator<number> {
        tween.to({ x: scale, y: scale }, duration).easing(Easing.Cubic.InOut);
        yield* this.startTween(tween);
    }

    private *rotateTo(
        tween: Tween,
        rotation: number,
        duration: number
    ): IterableIterator<number> {
        tween.to({ rotation: rotation }, duration);
        yield* this.startTween(tween);
    }
}
